import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, MessageSquare, Search } from 'lucide-react';

// Import functions from components.js
import { usePetitions, getParliamentaryTerms, updatePetitionStatus, formatTermDisplay, checkPetitionStatus, useParliamentaryTerms } from './components';

export const AllPetitionsPage = ({ onPetitionClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get('search') || '';
  const termFilter = searchParams.get('term');
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeTagFilter, setActiveTagFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [showFilters, setShowFilters] = useState(false);
  const [showTagFilters, setShowTagFilters] = useState(false);
  const [showTermFilters, setShowTermFilters] = useState(false);
  const [activeTermFilter, setActiveTermFilter] = useState('all');
  const [sortBy, setSortBy] = useState('most-signatures'); // Add sorting state
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh trigger
  const { petitions: allPetitions, loading } = usePetitions(); // Use reactive hook
  const { terms, loading: termsLoading } = useParliamentaryTerms();
  
  // Set initial term filter from URL parameter and handle URL changes
  useEffect(() => {
    if (termFilter) {
      setActiveTermFilter(termFilter);
      setShowTermFilters(true); // Open the term filters section when a term is selected
    } else {
      setActiveTermFilter('all');
    }
  }, [termFilter]);
  
  // Listen for petition updates to refresh the display
  useEffect(() => {
    const handlePetitionUpdate = () => {
      setRefreshKey(prev => prev + 1);
    };
    
    const handleTermUpdate = () => {
      setRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('petitionUpdated', handlePetitionUpdate);
    window.addEventListener('termUpdated', handleTermUpdate);
    
    return () => {
      window.removeEventListener('petitionUpdated', handlePetitionUpdate);
      window.removeEventListener('termUpdated', handleTermUpdate);
    };
  }, []);
  
  // Use the original tag filters from the backup
  const availableTagFilters = [
    'Budget',
    'Business',
    'Civil Service',
    'Defence',
    'Democracy',
    'Foreign Affairs',
    'Government',
    'Intelligence',
    'Justice',
    'Laws',
    'Parliament',
    'Public Services',
    'Rights',
    'Royal Family'
  ];

  // Enhanced filtering logic (original implementation)
  const filteredPetitions = allPetitions.filter(petition => {
    // Search filter
    if (initialSearch && !petition.title.toLowerCase().includes(initialSearch.toLowerCase()) && 
        !petition.description.toLowerCase().includes(initialSearch.toLowerCase())) {
      return false;
    }
    
    // Status filter  
    if (activeFilter !== 'all') {
      switch (activeFilter) {
        case 'open':
          // Open = still accepting signatures (not closed)
          // Includes: open, awaiting_government_response, government_response, 
          // awaiting_debate_in_parliament, debated_in_parliament, not_debated_in_parliament
          return petition.status !== 'closed';
        case 'closed':
          // Closed = explicitly closed status (no longer accepting signatures)
          return petition.status === 'closed';
        case 'awaiting-response':
          return petition.status === 'awaiting_government_response';
        case 'government-responses':
          return petition.governmentResponse || petition.status === 'government_response';
        case 'awaiting-debate':
          return petition.status === 'awaiting_debate_in_parliament';
        case 'debated':
          return petition.status === 'debated_in_parliament' || 
                 (petition.debateInfo && petition.debateInfo.status === 'debated');
        case 'not-debated':
          return petition.status === 'not_debated_in_parliament' || 
                 (petition.debateInfo && ['cancelled', 'not_debated'].includes(petition.debateInfo.status));
        default:
          return true;
      }
    }

    // Tag filter
    if (activeTagFilter !== 'all') {
      if (!petition.tags || !petition.tags.includes(activeTagFilter)) {
        return false;
      }
    }

    // Term filter - handle string/number comparison more safely
    if (termFilter && termFilter !== 'all') {
      // Convert both to strings for safe comparison
      if (String(petition.termId) !== String(termFilter)) {
        return false;
      }
    }

    return true;
  });

  // Sort petitions
  const sortedPetitions = [...filteredPetitions].sort((a, b) => {
    switch (sortBy) {
      case 'most-signatures':
        return (b.signatures || 0) - (a.signatures || 0);
      case 'newest':
        return new Date(b.created || 0) - new Date(a.created || 0);
      case 'oldest':
        return new Date(a.created || 0) - new Date(b.created || 0);
      case 'least-signatures':
        return (a.signatures || 0) - (b.signatures || 0);
      case 'alphabetical':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  // Calculate total petitions
  const totalPetitions = allPetitions.length;

  // Calculate statistics for original filter options (using petition status)
  const openCount = allPetitions.filter(p => p.status !== 'closed').length;
  const closedCount = allPetitions.filter(p => p.status === 'closed').length;
  const awaitingResponseCount = allPetitions.filter(p => p.status === 'awaiting_government_response').length;
  const responseCount = allPetitions.filter(p => p.governmentResponse || p.status === 'government_response').length;
  const awaitingDebateCount = allPetitions.filter(p => 
    p.status === 'awaiting_debate_in_parliament' || 
    (p.signatures >= 20 && !p.debateInfo)
  ).length;
  const debatedCount = allPetitions.filter(p => 
    p.status === 'debated_in_parliament' || 
    (p.debateInfo && p.debateInfo.status === 'debated')
  ).length;
  const notDebatedCount = allPetitions.filter(p => 
    p.status === 'not_debated_in_parliament' || 
    (p.debateInfo && ['cancelled', 'not_debated'].includes(p.debateInfo.status))
  ).length;

  // Create filter options with counts (original implementation)
  const filters = [
    { key: 'all', label: 'All petitions', count: allPetitions.length },
    { key: 'open', label: 'Open petitions', count: openCount },
    { key: 'closed', label: 'Closed petitions', count: closedCount },
    { key: 'awaiting-response', label: 'Awaiting government response', count: awaitingResponseCount },
    { key: 'government-responses', label: 'Government responses', count: responseCount },
    { key: 'awaiting-debate', label: 'Awaiting a debate in Parliament', count: awaitingDebateCount },
    { key: 'debated', label: 'Debated in Parliament', count: debatedCount },
    { key: 'not-debated', label: 'Not debated in Parliament', count: notDebatedCount }
  ];

  // Create tag filter options with counts
  const tagFilters = [
    { key: 'all', label: 'All tags', count: totalPetitions },
    ...availableTagFilters.map(tag => ({
      key: tag,
      label: tag,
      count: allPetitions.filter(p => p.tags && p.tags.includes(tag)).length
    }))
  ];

  // Create term filter options with counts - include both database terms and any terms found in petitions
  const predefinedTerms = (terms || []).map(term => ({
    key: term.id,
    label: formatTermDisplay(term),
    termId: term.id,
    count: allPetitions.filter(p => String(p.termId) === String(term.id)).length,
    isPredefined: true
  }));

  // Find any additional terms that exist in petitions but not in database terms
  const petitionTermIds = [...new Set(allPetitions.map(p => p.termId).filter(Boolean))];
  const additionalTerms = petitionTermIds
    .filter(termId => !(terms || []).find(t => String(t.id) === String(termId)))
    .map(termId => ({
      key: termId,
      label: `Term ${termId} (Unknown)`,
      termId: termId,
      count: allPetitions.filter(p => String(p.termId) === String(termId)).length,
      isPredefined: false
    }));

  const termFilters = [
    { key: 'all', label: 'All terms', count: totalPetitions },
    ...predefinedTerms.filter(term => term.count > 0), // Only show terms that have petitions
    ...additionalTerms
  ];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/petitions?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/petitions');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePetitionClick = (petition) => {
    if (onPetitionClick) {
      onPetitionClick(petition);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue Header */}
      <div className="bg-blue-600 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-blue-100 hover:text-white mb-4 transition-colors"
            >
              ← Back to home
            </button>
          </div>
          <h1 className="text-3xl font-bold mb-2">All petitions</h1>
          <p className="text-blue-100">Browse and search all petitions</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Desktop Sidebar Filters (hidden on mobile) */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            {/* Sort Dropdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort by
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="most-signatures">Most signatures</option>
                <option value="newest">Most recently created</option>
                <option value="oldest">Oldest first</option>
                <option value="least-signatures">Fewest signatures</option>
                <option value="alphabetical">Alphabetical (A-Z)</option>
              </select>
            </div>

            {/* Filter petitions (collapsible) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <span>Filter petitions</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${showFilters ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showFilters && (
                <div className="divide-y divide-gray-200">
                  {filters.map((filter) => (
                    <button
                      key={filter.key}
                      onClick={() => setActiveFilter(filter.key)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        activeFilter === filter.key ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500' : 'text-gray-700'
                      }`}
                    >
                      <span>{filter.label}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activeFilter === filter.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {filter.count.toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter by tags */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
              <button
                onClick={() => setShowTagFilters(!showTagFilters)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <span>Filter by tags</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${showTagFilters ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showTagFilters && (
                <div className="divide-y divide-gray-200">
                  {tagFilters.map((tagFilter) => (
                    <button
                      key={tagFilter.key}
                      onClick={() => setActiveTagFilter(tagFilter.key)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        activeTagFilter === tagFilter.key ? 'bg-green-50 text-green-700 border-l-4 border-green-500' : 'text-gray-700'
                      }`}
                    >
                      <span>{tagFilter.label}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activeTagFilter === tagFilter.key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {tagFilter.count.toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter by parliamentary term */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
              <button
                onClick={() => setShowTermFilters(!showTermFilters)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <span>Filter by parliamentary term</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${showTermFilters ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showTermFilters && (
                <div className="divide-y divide-gray-200">
                  {termFilters.map((termFilter) => (
                    <button
                      key={termFilter.key}
                      onClick={() => {
                        if (termFilter.termId) {
                          setActiveTermFilter(String(termFilter.termId));
                          navigate(`/petitions?term=${termFilter.termId}`);
                        } else {
                          setActiveTermFilter('all');
                          navigate('/petitions');
                        }
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        String(activeTermFilter) === String(termFilter.termId || 'all') ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500' : 'text-gray-700'
                      }`}
                    >
                      <span>{termFilter.label}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        String(activeTermFilter) === String(termFilter.termId || 'all') ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {termFilter.count.toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 order-1 lg:order-2">
            {/* Search Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 mb-6">
              <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">Search petitions</h2>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                <input
                  type="text"
                  placeholder="Search for petitions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-md sm:rounded-l-md sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button 
                  onClick={handleSearch}
                  className="bg-green-600 text-white px-6 py-3 rounded-md sm:rounded-l-none sm:rounded-r-md hover:bg-green-700 transition-colors border border-green-600 flex items-center justify-center min-w-[120px] sm:min-w-0"
                >
                  <Search className="w-5 h-5 mr-2 sm:mr-0" />
                  <span className="sm:hidden">Search</span>
                </button>
              </div>
            </div>

            {/* Mobile Filters Section (shown only on mobile) */}
            <div className="lg:hidden mb-6 space-y-4">
              {/* Sort Dropdown */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="most-signatures">Most signatures</option>
                  <option value="newest">Most recently created</option>
                  <option value="oldest">Oldest first</option>
                  <option value="least-signatures">Fewest signatures</option>
                  <option value="alphabetical">Alphabetical (A-Z)</option>
                </select>
              </div>

              {/* Mobile Filter petitions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <span>Filter petitions</span>
                  <span className="transform transition-transform duration-200" style={{
                    transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ↓
                  </span>
                </button>
                
                {showFilters && (
                  <div className="bg-white">
                    {filters.map((filter) => (
                      <button
                        key={filter.key}
                        onClick={() => setActiveFilter(filter.key)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          activeFilter === filter.key ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500' : 'text-gray-700'
                        }`}
                      >
                        <span>{filter.label}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activeFilter === filter.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {filter.count.toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Filter by tags */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setShowTagFilters(!showTagFilters)}
                  className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <span>Filter by tags</span>
                  <span className="transform transition-transform duration-200" style={{
                    transform: showTagFilters ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ↓
                  </span>
                </button>
                
                {showTagFilters && (
                  <div className="bg-white">
                    {tagFilters.map((tagFilter) => (
                      <button
                        key={tagFilter.key}
                        onClick={() => setActiveTagFilter(tagFilter.key)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          activeTagFilter === tagFilter.key ? 'bg-green-50 text-green-700 border-l-4 border-green-500' : 'text-gray-700'
                        }`}
                      >
                        <span>{tagFilter.label}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activeTagFilter === tagFilter.key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tagFilter.count.toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Filter by parliamentary term */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setShowTermFilters(!showTermFilters)}
                  className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <span>Filter by parliamentary term</span>
                  <span className="transform transition-transform duration-200" style={{
                    transform: showTermFilters ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ↓
                  </span>
                </button>
                
                {showTermFilters && (
                  <div className="bg-white">
                    {termFilters.map((termFilter) => (
                      <button
                        key={termFilter.key}
                        onClick={() => {
                          if (termFilter.termId) {
                            setActiveTermFilter(String(termFilter.termId));
                            navigate(`/petitions?term=${termFilter.termId}`);
                          } else {
                            setActiveTermFilter('all');
                            navigate('/petitions');
                          }
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          String(activeTermFilter) === String(termFilter.termId || 'all') ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500' : 'text-gray-700'
                        }`}
                      >
                        <span>{termFilter.label}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          String(activeTermFilter) === String(termFilter.termId || 'all') ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {termFilter.count.toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-600">
                Showing {filteredPetitions.length} petition{filteredPetitions.length !== 1 ? 's' : ''}
                {searchQuery && ` for "${searchQuery}"`}
                {activeFilter !== 'all' && ` in ${filters.find(f => f.key === activeFilter)?.label.toLowerCase()}`}
                {termFilter && (() => {
                  const term = (terms || []).find(t => String(t.id) === String(termFilter));
                  return term ? ` from ${formatTermDisplay(term)}` : '';
                })()}
              </p>
            </div>

            {/* Petitions List */}
            <div className="space-y-4">
              {loading || termsLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading petitions and parliamentary terms...</p>
                </div>
              ) : sortedPetitions.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <p className="text-gray-500 text-lg">No petitions match your current filters.</p>
                  <p className="text-gray-400 mt-2">Try adjusting your search terms or filters.</p>
                </div>
              ) : (
                sortedPetitions.map((petition) => (
                  <div
                    key={petition.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer p-4 lg:p-6"
                    onClick={() => handlePetitionClick(petition)}
                  >
                    <div>
                      {/* Use PopularPetitions format exactly */}
                      <h3 className="text-lg font-semibold text-blue-600 mb-2 hover:underline">
                        {petition.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2 text-sm lg:text-base">
                        {petition.description}
                      </p>
                      
                      {/* Government Response */}
                      {petition.governmentResponse && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 px-4 py-3 mb-4">
                          <div className="flex items-center mb-1">
                            <MessageSquare className="w-4 h-4 text-blue-600 mr-2" />
                            <span className="text-blue-800 font-medium text-sm">Government response available</span>
                          </div>
                          <p className="text-blue-700 text-sm">{typeof petition.governmentResponse === 'object' ? petition.governmentResponse.text || petition.governmentResponse.content || JSON.stringify(petition.governmentResponse) : petition.governmentResponse}</p>
                          {petition.governmentResponseMeta && (
                            <div className="text-xs text-blue-600 italic mt-2">
                              Added by {petition.governmentResponseMeta.addedBy} ({petition.governmentResponseMeta.addedByRole})
                            </div>
                          )}
                        </div>
                      )}

                      {/* Parliament Debate */}
                      {petition.debateInfo && (
                        <div className="bg-purple-50 border-l-4 border-purple-500 px-4 py-3 mb-4">
                          <div className="flex items-center mb-1">
                            <MessageSquare className="w-4 h-4 text-purple-600 mr-2" />
                            <span className="text-purple-800 font-medium text-sm">
                              {petition.debateInfo.status === 'debated' && 'Parliament debate completed'}
                              {petition.debateInfo.status === 'scheduled' && 'Parliament debate scheduled'}
                              {petition.debateInfo.status === 'cancelled' && 'Parliament debate cancelled'}
                              {petition.debateInfo.status === 'not_debated' && 'Will not be debated in parliament'}
                            </span>
                          </div>
                          {petition.debateInfo.status === 'not_debated' && petition.debateInfo.notDebatedReason && (
                            <p className="text-purple-700 text-sm">{petition.debateInfo.notDebatedReason}</p>
                          )}
                          {petition.debateInfo.status !== 'not_debated' && (
                            <p className="text-purple-700 text-sm">
                              {new Date(petition.debateInfo.date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                          {petition.debateInfoMeta && (
                            <div className="text-xs text-purple-600 italic mt-2">
                              Added by {petition.debateInfoMeta.addedBy} ({petition.debateInfoMeta.addedByRole})
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Created: {(() => {
                            const dateValue = petition.created || petition.createdAt;
                            if (typeof dateValue === 'object' && dateValue.date) {
                              return new Date(dateValue.date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              });
                            }
                            return new Date(dateValue).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            });
                          })()}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>
                            {typeof petition.signatures === 'object' ? (petition.signatures.count || 0) : petition.signatures.toLocaleString()} signatures
                          </span>
                          <span>•</span>
                          <span>{petition.tags ? (Array.isArray(petition.tags) ? petition.tags.map(tag => typeof tag === 'object' ? tag.name || JSON.stringify(tag) : tag).join(', ') : petition.tags) : 'No tags'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};